import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { PageHeader } from '@resource-kit/ui/react';

const Unauthorized: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  /**
   * In-flight marker for the sign-out. Local rather than `useAuth().isLoading`
   * on purpose: this is the ONLY escape from this page, so its feedback must
   * not depend on a flag shared with sign-in and replaceable by the caller.
   *
   * It drives `disabled`, which is also the whole double-click guard — a
   * disabled button dispatches no click, so a second `signOut()` cannot be
   * fired. No separate re-entry check: a redundant one is unfalsifiable.
   *
   * Never reset to `false` — every outcome of the promise navigates away, so
   * the control is terminal once armed (and resetting after the unmount that
   * navigation causes would be a no-op anyway).
   */
  const [isSigningOut, setIsSigningOut] = useState(false);

  /**
   * This page is a landing destination for a seat whose grants match no
   * enterable route, and it sits OUTSIDE `AppLayout` — so there is no user
   * menu and no sign-out control anywhere on it. Navigating to `/login` while
   * still authenticated is not an escape either: `GuestGuard` bounces an
   * authenticated visitor straight back through `LandingRedirect`. So the
   * session must be cleared FIRST, then the login form requested explicitly
   * (nothing re-navigates a route that no guard owns).
   */
  const handleSwitchAccount = () => {
    setIsSigningOut(true);
    void signOut()
      .catch(() => {
        // The auth context clears local session state in its own `finally`,
        // so even a failed network sign-out ends the session client-side.
        // There is nothing to recover here — the navigation below is the
        // user's escape either way. That load-bearing `finally` is pinned by
        // "keeps clearing … when the sign-out request fails" in
        // src/contexts/__tests__/BetterAuthContext.test.tsx; the escape itself
        // is pinned by src/__tests__/appRoutes.unauthorizedEscape.test.tsx.
      })
      .finally(() => {
        navigate('/login', { replace: true });
      });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center">
        <PageHeader
          align="center"
          title={t('auth.unauthorized.title')}
          description={t('auth.unauthorized.message')}
        />
        <div className="mt-6 flex gap-3 justify-center">
          <Link
            to="/"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {t('auth.unauthorized.goHome')}
          </Link>
          <button
            type="button"
            data-testid="unauthorized-sign-out"
            onClick={handleSwitchAccount}
            disabled={isSigningOut}
            aria-busy={isSigningOut}
            className="px-4 py-2 rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {t('auth.unauthorized.switchAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Unauthorized;
